import { LitElement, html, css } from 'lit';

export class GameMode extends LitElement {
  static styles = css`
    :host {
      display: block;
    }

    .game-container {
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      border-radius: 24px;
      padding: 24px;
      box-shadow: 0 20px 60px rgba(102, 126, 234, 0.3);
      position: relative;
      overflow: hidden;
    }

    .game-container::before {
      content: '';
      position: absolute;
      top: -50%;
      left: -50%;
      width: 200%;
      height: 200%;
      background: radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 60%);
      animation: rotate 30s linear infinite;
    }

    @keyframes rotate {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }

    .game-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 24px;
      position: relative;
      z-index: 1;
    }

    .game-title {
      font-size: 1.5rem;
      font-weight: 800;
      color: white;
      text-shadow: 0 2px 4px rgba(0,0,0,0.2);
    }

    .progress-wrapper {
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      gap: 6px;
    }

    .matches-count {
      font-size: 0.875rem;
      font-weight: 700;
      color: white;
      background: rgba(255,255,255,0.2);
      padding: 6px 14px;
      border-radius: 20px;
      backdrop-filter: blur(10px);
    }

    .progress-bar {
      width: 140px;
      height: 10px;
      background: rgba(255,255,255,0.2);
      border-radius: 5px;
      overflow: hidden;
      box-shadow: inset 0 2px 4px rgba(0,0,0,0.1);
    }

    .progress-fill {
      height: 100%;
      background: linear-gradient(90deg, #ffd700 0%, #ffed4e 100%);
      border-radius: 5px;
      transition: width 0.4s cubic-bezier(0.4, 0, 0.2, 1);
      box-shadow: 0 0 10px rgba(255, 215, 0, 0.5);
    }

    .game-area {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 20px;
      position: relative;
      z-index: 1;
    }

    .column {
      display: flex;
      flex-direction: column;
      gap: 12px;
    }

    .column-header {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      margin-bottom: 8px;
    }

    .column-flag {
      font-size: 1.5rem;
    }

    .column-title {
      font-size: 0.875rem;
      font-weight: 700;
      color: rgba(255,255,255,0.9);
      text-transform: uppercase;
      letter-spacing: 2px;
    }

    .word-card {
      padding: 18px 16px;
      background: white;
      border: none;
      border-radius: 16px;
      cursor: pointer;
      transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
      font-size: 0.95rem;
      font-weight: 500;
      text-align: center;
      min-height: 60px;
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 4px 15px rgba(0,0,0,0.1);
      position: relative;
      overflow: hidden;
    }

    .word-card::after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: linear-gradient(135deg, transparent 0%, rgba(255,255,255,0.4) 50%, transparent 100%);
      transform: translateX(-100%);
      transition: transform 0.6s;
    }

    .word-card:hover:not(.selected):not(.wrong) {
      transform: translateY(-4px) scale(1.02);
      box-shadow: 0 12px 30px rgba(0,0,0,0.2);
    }

    .word-card:hover::after {
      transform: translateX(100%);
    }

    .word-card.selected {
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: white;
      transform: scale(1.05);
      box-shadow: 0 8px 25px rgba(102, 126, 234, 0.5);
    }

    .word-card.matched {
      animation: matchSuccess 0.5s ease forwards;
    }

    @keyframes matchSuccess {
      0% { 
        transform: scale(1.05);
        opacity: 1;
      }
      50% {
        transform: scale(1.1);
        background: linear-gradient(135deg, #4caf50 0%, #8bc34a 100%);
        color: white;
      }
      100% { 
        transform: scale(0);
        opacity: 0;
      }
    }

    .word-card.wrong {
      animation: shake 0.5s ease;
      background: linear-gradient(135deg, #f44336 0%, #ff5722 100%);
      color: white;
    }

    @keyframes shake {
      0%, 100% { transform: translateX(0); }
      20% { transform: translateX(-10px); }
      40% { transform: translateX(10px); }
      60% { transform: translateX(-6px); }
      80% { transform: translateX(6px); }
    }

    .instructions {
      text-align: center;
      color: rgba(255,255,255,0.95);
      font-size: 1rem;
      margin: 16px 0;
      min-height: 28px;
      font-weight: 500;
      position: relative;
      z-index: 1;
      text-shadow: 0 1px 2px rgba(0,0,0,0.2);
    }

    .instructions strong {
      color: #ffd700;
    }

    .streak-indicator {
      position: absolute;
      top: 24px;
      left: 50%;
      transform: translateX(-50%);
      background: linear-gradient(135deg, #ffd700 0%, #ffed4e 100%);
      color: #333;
      padding: 8px 20px;
      border-radius: 20px;
      font-weight: 700;
      font-size: 0.9rem;
      box-shadow: 0 4px 15px rgba(255, 215, 0, 0.4);
      opacity: 0;
      transition: all 0.3s ease;
      z-index: 10;
    }

    .streak-indicator.show {
      opacity: 1;
      animation: pulse 0.5s ease;
    }

    @keyframes pulse {
      0%, 100% { transform: translateX(-50%) scale(1); }
      50% { transform: translateX(-50%) scale(1.1); }
    }

    .results {
      text-align: center;
      padding: 40px 20px;
      position: relative;
      z-index: 1;
    }

    .trophy {
      font-size: 5rem;
      margin-bottom: 20px;
      animation: bounce 1s ease infinite;
    }

    @keyframes bounce {
      0%, 100% { transform: translateY(0); }
      50% { transform: translateY(-10px); }
    }

    .results-title {
      font-size: 2rem;
      font-weight: 800;
      color: white;
      margin-bottom: 12px;
      text-shadow: 0 2px 4px rgba(0,0,0,0.2);
    }

    .results-text {
      font-size: 1.25rem;
      color: rgba(255,255,255,0.9);
      margin-bottom: 32px;
    }

    .play-again-btn {
      padding: 18px 40px;
      background: white;
      color: #667eea;
      border: none;
      border-radius: 16px;
      font-size: 1.1rem;
      font-weight: 700;
      cursor: pointer;
      transition: all 0.3s ease;
      box-shadow: 0 8px 25px rgba(0,0,0,0.2);
    }

    .play-again-btn:hover {
      transform: translateY(-4px) scale(1.02);
      box-shadow: 0 15px 40px rgba(0,0,0,0.3);
    }

    .confetti {
      position: absolute;
      width: 10px;
      height: 10px;
      animation: confetti-fall 3s ease-out forwards;
    }

    @keyframes confetti-fall {
      0% {
        transform: translateY(-100px) rotate(0deg);
        opacity: 1;
      }
      100% {
        transform: translateY(400px) rotate(720deg);
        opacity: 0;
      }
    }

    @media (max-width: 480px) {
      .game-container {
        padding: 20px 16px;
      }

      .game-title {
        font-size: 1.25rem;
      }

      .word-card {
        padding: 14px 12px;
        font-size: 0.85rem;
        min-height: 52px;
        border-radius: 12px;
      }

      .game-area {
        gap: 12px;
      }

      .column-flag {
        font-size: 1.25rem;
      }

      .column-title {
        font-size: 0.75rem;
        letter-spacing: 1px;
      }

      .progress-bar {
        width: 100px;
      }
    }
  `;

  static properties = {
    allWords: { type: Array },
    dutchColumn: { type: Array },
    italianColumn: { type: Array },
    selectedDutch: { type: Object },
    selectedItalian: { type: Object },
    matchedPairs: { type: Number },
    totalMatches: { type: Number },
    isComplete: { type: Boolean },
    wrongMatch: { type: Object },
    streak: { type: Number },
    showStreak: { type: Boolean }
  };

  constructor() {
    super();
    this.allWords = [];
    this.dutchColumn = [];
    this.italianColumn = [];
    this.selectedDutch = null;
    this.selectedItalian = null;
    this.matchedPairs = 0;
    this.totalMatches = 50;
    this.isComplete = false;
    this.wrongMatch = null;
    this.streak = 0;
    this.showStreak = false;
    this.usedWordIds = new Set();
  }

  updated(changedProperties) {
    if (changedProperties.has('allWords') && this.allWords.length > 0) {
      this._initGame();
    }
  }

  _initGame() {
    this.matchedPairs = 0;
    this.isComplete = false;
    this.streak = 0;
    this.usedWordIds.clear();
    this.dutchColumn = [];
    this.italianColumn = [];
    
    // Fill initial board with 5 pairs
    for (let i = 0; i < 5; i++) {
      this._addNewPair();
    }
  }

  _getRandomUnusedWord() {
    const availableWords = this.allWords.filter(w => !this.usedWordIds.has(w.id));
    if (availableWords.length === 0) {
      // Reset used words if all have been used
      this.usedWordIds.clear();
      return this.allWords[Math.floor(Math.random() * this.allWords.length)];
    }
    return availableWords[Math.floor(Math.random() * availableWords.length)];
  }

  _addNewPair() {
    if (this.matchedPairs >= this.totalMatches) return;
    
    const word = this._getRandomUnusedWord();
    if (!word) return;
    
    this.usedWordIds.add(word.id);
    
    // Create IDs that include position to ensure uniqueness
    const timestamp = Date.now();
    const dutchId = `${word.id}_nl_${timestamp}`;
    const italianId = `${word.id}_it_${timestamp}`;
    
    const dutchItem = {
      id: dutchId,
      text: word.word,
      matchId: word.id,
      uniqueKey: dutchId
    };
    
    const italianItem = {
      id: italianId,
      text: word.meaning,
      matchId: word.id,
      uniqueKey: italianId
    };
    
    // Add to columns
    this.dutchColumn = [...this.dutchColumn, dutchItem];
    this.italianColumn = [...this.italianColumn, italianItem];
    
    // Shuffle columns
    this.dutchColumn = [...this.dutchColumn].sort(() => Math.random() - 0.5);
    this.italianColumn = [...this.italianColumn].sort(() => Math.random() - 0.5);
  }

  _selectDutch(item) {
    if (this.selectedDutch?.uniqueKey === item.uniqueKey) {
      this.selectedDutch = null;
      return;
    }
    this.selectedDutch = item;
    this._checkMatch();
  }

  _selectItalian(item) {
    if (this.selectedItalian?.uniqueKey === item.uniqueKey) {
      this.selectedItalian = null;
      return;
    }
    this.selectedItalian = item;
    this._checkMatch();
  }

  _checkMatch() {
    if (!this.selectedDutch || !this.selectedItalian) return;

    if (this.selectedDutch.matchId === this.selectedItalian.matchId) {
      // Correct match!
      this._playSuccessSound();
      this.matchedPairs++;
      this.streak++;
      this.showStreak = true;
      setTimeout(() => this.showStreak = false, 1500);
      
      // Mark items as matched (for animation)
      const dutchKey = this.selectedDutch.uniqueKey;
      const italianKey = this.selectedItalian.uniqueKey;
      
      // Remove matched items after animation
      setTimeout(() => {
        this.dutchColumn = this.dutchColumn.filter(item => item.uniqueKey !== dutchKey);
        this.italianColumn = this.italianColumn.filter(item => item.uniqueKey !== italianKey);
        
        // Add new pair immediately to replace the matched one
        this._addNewPair();
      }, 400);
      
      this.selectedDutch = null;
      this.selectedItalian = null;

      // Check if game is complete
      if (this.matchedPairs >= this.totalMatches) {
        setTimeout(() => this.isComplete = true, 500);
      }

      // Dispatch progress event
      this.dispatchEvent(new CustomEvent('match', {
        detail: { matched: this.matchedPairs, total: this.totalMatches },
        bubbles: true,
        composed: true
      }));
    } else {
      // Wrong match - reset streak
      this.streak = 0;
      this.wrongMatch = { 
        dutch: this.selectedDutch.uniqueKey, 
        italian: this.selectedItalian.uniqueKey 
      };
      setTimeout(() => {
        this.wrongMatch = null;
        this.selectedDutch = null;
        this.selectedItalian = null;
      }, 500);
    }
  }

  _playSuccessSound() {
    const audioContext = new (window.AudioContext || window.webkitAudioContext);
    const now = audioContext.currentTime;
    
    // Japanese wooden block (mokugyo/woodblock) inspired sound
    const createWoodTone = (freq, delay, volume) => {
      // Main wooden body sound
      const osc = audioContext.createOscillator();
      const gain = audioContext.createGain();
      const filter = audioContext.createBiquadFilter();
      
      osc.connect(filter);
      filter.connect(gain);
      gain.connect(audioContext.destination);
      
      // Triangle wave gives woody harmonics
      osc.type = 'triangle';
      osc.frequency.setValueAtTime(freq, now + delay);
      
      // Bandpass to emphasize the wooden resonance
      filter.type = 'bandpass';
      filter.frequency.setValueAtTime(freq * 2, now + delay);
      filter.Q.setValueAtTime(0.5, now + delay);
      
      // Soft wooden envelope
      gain.gain.setValueAtTime(0, now + delay);
      gain.gain.linearRampToValueAtTime(volume, now + delay + 0.005);
      gain.gain.exponentialRampToValueAtTime(0.001, now + delay + 0.4);
      
      osc.start(now + delay);
      osc.stop(now + delay + 0.5);
    };
    
    // Create a soft "kon-kon" pattern like a wooden temple block
    // Lower frequencies for that deep wood resonance
    createWoodTone(350, 0, 0.25);      // Main tone
    createWoodTone(520, 0.08, 0.12);   // Higher harmonic, delayed
  }

  _restartGame() {
    this._initGame();
    this.dispatchEvent(new CustomEvent('restart', {
      bubbles: true,
      composed: true
    }));
  }

  render() {
    if (this.isComplete) {
      return html`
        <div class="game-container">
          <div class="results">
            <div class="trophy">🏆</div>
            <div class="results-title">Gefeliciteerd!</div>
            <div class="results-text">
              Je hebt alle ${this.totalMatches} woordenparen gematcht!
            </div>
            <button class="play-again-btn" @click="${this._restartGame}">
              Opnieuw Spelen
            </button>
          </div>
        </div>
      `;
    }

    if (!this.allWords.length) {
      return html`
        <div class="game-container">
          <p style="text-align: center; color: white;">Woorden laden...</p>
        </div>
      `;
    }

    const progressPercent = Math.min((this.matchedPairs / this.totalMatches) * 100, 100);

    return html`
      <div class="game-container">
        <div class="streak-indicator ${this.showStreak && this.streak > 1 ? 'show' : ''}">
          🔥 ${this.streak} Serie!
        </div>
        
        <div class="game-header">
          <div class="game-title">⚡ Match Spel</div>
          <div class="progress-wrapper">
            <span class="matches-count">${this.matchedPairs}/${this.totalMatches}</span>
            <div class="progress-bar">
              <div class="progress-fill" style="width: ${progressPercent}%"></div>
            </div>
          </div>
        </div>

        <div class="instructions">
          ${this.selectedDutch 
            ? html`Geselecteerd: <strong>${this.selectedDutch.text}</strong> — vind de vertaling!`
            : html`Tap een 🇳🇱 Nederlands woord, dan de 🇮🇹 Italiaanse match`}
        </div>

        <div class="game-area">
          <div class="column">
            <div class="column-header">
              <span class="column-flag">🇳🇱</span>
              <span class="column-title">Nederlands</span>
            </div>
            ${this.dutchColumn.map(item => html`
              <button
                class="word-card 
                  ${this.selectedDutch?.uniqueKey === item.uniqueKey ? 'selected' : ''} 
                  ${this.wrongMatch?.dutch === item.uniqueKey ? 'wrong' : ''}"
                @click="${() => this._selectDutch(item)}"
              >
                ${item.text}
              </button>
            `)}
          </div>
          <div class="column">
            <div class="column-header">
              <span class="column-flag">🇮🇹</span>
              <span class="column-title">Italiaans</span>
            </div>
            ${this.italianColumn.map(item => html`
              <button
                class="word-card 
                  ${this.selectedItalian?.uniqueKey === item.uniqueKey ? 'selected' : ''} 
                  ${this.wrongMatch?.italian === item.uniqueKey ? 'wrong' : ''}"
                @click="${() => this._selectItalian(item)}"
              >
                ${item.text}
              </button>
            `)}
          </div>
        </div>
      </div>
    `;
  }
}

customElements.define('game-mode', GameMode);
